(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('nl-price-grade')) return;

  var MIN_LISTING_PRICE = 135;
  // The suggested-price wording test ran and the speed-of-sale framing won, so
  // it is now the only wording the widget renders: every grade gets its
  // sales-framed label plus a one-line helper. There is no arm, no
  // copy-variant attribute and no fallback to the old grade labels.
  var PRICE_GRADE_COPY = {
    incredible: {
      label: 'Quick sale likely',
      helper: 'Priced below similar bikes, so it should attract buyers fast.'
    },
    great: {
      label: 'Strong price',
      helper: 'In line with what similar bikes sell for.'
    },
    good: {
      label: 'Good Price',
      helper: 'A little above similar bikes. It may take slightly longer to sell.'
    },
    overpriced: {
      label: 'Slower sale likely',
      helper: 'Above what similar bikes usually sell for. Lowering the price could help it sell sooner.'
    }
  };

  // Mirrors commission.ts.
  function resolveRate(price) {
    return price >= 2640 ? 0.12 : price >= 1740 ? 0.13 : 0.14;
  }

  function applyCommission(price) {
    return Math.round(price / (1 - resolveRate(price)));
  }

  function getCommissionPercent(price) {
    return Math.round(resolveRate(price) * 100);
  }

  function formatPrice(price) {
    return '£' + Math.round(price).toLocaleString('en-GB');
  }

  // Mirrors priceIndicator.ts getGradientPosition — 30% below suggested price
  // maps to 0, suggested price maps to 50, 30% above maps to 100.
  function getGradientPosition(price, suggestedPrice) {
    var lowEnd = suggestedPrice * 0.7;
    var highEnd = suggestedPrice * 1.3;
    var position = ((price - lowEnd) / (highEnd - lowEnd)) * 100;
    return Math.max(0, Math.min(100, position));
  }

  // Mirrors priceIndicator.ts getPriceBadge. Colors are not carried over —
  // the host page renders color from [data-grade] via Ink tokens instead.
  function getPriceBadge(listedPrice, suggestedPrice) {
    if (!suggestedPrice || listedPrice <= 0) return null;
    var percentDiff = (listedPrice - suggestedPrice) / suggestedPrice;
    var score = Math.max(1, Math.min(10, Math.round(5 - percentDiff * 10)));
    if (score >= 7) return { label: 'Incredible Price', grade: 'incredible' };
    if (score >= 5) return { label: 'Great Price', grade: 'great' };
    if (score >= 3) return { label: 'Good Price', grade: 'good' };
    return { label: 'Overpriced', grade: 'overpriced' };
  }

  // Mirrors PricingSection.tsx trackGradientStops — must stay in sync with
  // the --slider-track linear-gradient set in _update().
  var TRACK_GRADIENT_STOPS = [
    { pos: 0, rgb: [34, 197, 94] },
    { pos: 30, rgb: [34, 197, 94] },
    { pos: 50, rgb: [234, 179, 8] },
    { pos: 80, rgb: [142, 153, 163] },
    { pos: 100, rgb: [142, 153, 163] }
  ];

  function sampleTrackGradient(position) {
    var pos = Math.max(0, Math.min(100, position));
    for (var i = 0; i < TRACK_GRADIENT_STOPS.length - 1; i++) {
      var start = TRACK_GRADIENT_STOPS[i];
      var end = TRACK_GRADIENT_STOPS[i + 1];
      if (pos >= start.pos && pos <= end.pos) {
        var t = end.pos === start.pos ? 0 : (pos - start.pos) / (end.pos - start.pos);
        var r = Math.round(start.rgb[0] + (end.rgb[0] - start.rgb[0]) * t);
        var g = Math.round(start.rgb[1] + (end.rgb[1] - start.rgb[1]) * t);
        var b = Math.round(start.rgb[2] + (end.rgb[2] - start.rgb[2]) * t);
        return 'rgb(' + r + ', ' + g + ', ' + b + ')';
      }
    }
    return 'rgb(142, 153, 163)';
  }

  class NlPriceGrade extends HTMLElement {
    static get observedAttributes() {
      return ['listing-price', 'suggested-price'];
    }

    constructor() {
      super();
      this._built = false;
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this._render();
      this._update();
    }

    attributeChangedCallback() {
      if (this._built) this._update();
    }

    _render() {
      this.classList.add('nl-price-grade-widget');
      this.textContent = '';

      var sliderWrap = document.createElement('div');
      sliderWrap.className = 'nl-price-slider-wrap';

      var slider = document.createElement('input');
      slider.type = 'range';
      slider.className = 'nl-price-slider';
      slider.setAttribute('data-price-slider', '');
      slider.min = '0';
      slider.max = '0';
      slider.step = '10';
      slider.setAttribute('aria-label', 'Adjust seller asking price');
      sliderWrap.appendChild(slider);

      var tick = document.createElement('span');
      tick.className = 'nl-price-tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.hidden = true;
      sliderWrap.appendChild(tick);

      var badge = document.createElement('span');
      badge.className = 'nl-price-badge';
      badge.hidden = true;

      var helper = document.createElement('p');
      helper.className = 'nl-help nl-price-helper';
      helper.hidden = true;

      var commission = document.createElement('span');
      commission.className = 'nl-help nl-price-commission';
      commission.hidden = true;

      this.appendChild(sliderWrap);
      this.appendChild(badge);
      this.appendChild(helper);
      this.appendChild(commission);

      this._slider = slider;
      this._tick = tick;
      this._badge = badge;
      this._helper = helper;
      this._commission = commission;
    }

    _update() {
      var listingPrice = parseFloat(this.getAttribute('listing-price') || '0');
      var suggestedPrice = parseFloat(this.getAttribute('suggested-price') || '0');

      if (suggestedPrice > 0) {
        this._slider.min = String(Math.round(suggestedPrice * 0.5));
        this._slider.max = String(Math.round(suggestedPrice * 1.5));
        this._slider.value = String(listingPrice || Math.round(suggestedPrice));

        var thumbPosition = getGradientPosition(listingPrice || suggestedPrice, suggestedPrice);
        this._slider.style.setProperty(
          '--slider-track',
          'linear-gradient(to right, rgb(34, 197, 94) 0%, rgb(34, 197, 94) 30%, rgb(234, 179, 8) 50%, rgb(142, 153, 163) 80%, rgb(142, 153, 163) 100%)'
        );
        this._slider.style.setProperty('--slider-range', 'transparent');
        this._slider.style.setProperty('--slider-thumb', sampleTrackGradient(thumbPosition));
        this._slider.style.setProperty('--slider-thumb-border', 'white');

        var incrediblePrice = suggestedPrice * 0.85;
        var sliderMin = suggestedPrice * 0.5;
        var sliderMax = suggestedPrice * 1.5;
        var indicatorPosition = sliderMax > sliderMin
          ? ((incrediblePrice - sliderMin) / (sliderMax - sliderMin)) * 100
          : 0;
        if (indicatorPosition >= 0 && indicatorPosition <= 100) {
          this._tick.hidden = false;
          this._tick.style.left = indicatorPosition + '%';
        } else {
          this._tick.hidden = true;
        }
      } else {
        this._slider.min = '0';
        this._slider.max = '0';
        this._slider.value = '0';
        this._tick.hidden = true;
      }

      var badgeInfo = getPriceBadge(listingPrice, suggestedPrice);
      var copy = badgeInfo ? PRICE_GRADE_COPY[badgeInfo.grade] : null;
      this._badge.hidden = !badgeInfo;
      this._badge.textContent = copy ? copy.label : '';
      if (badgeInfo) this._badge.setAttribute('data-grade', badgeInfo.grade);
      else this._badge.removeAttribute('data-grade');

      this._helper.hidden = !copy;
      this._helper.textContent = copy ? copy.helper : '';

      if (listingPrice >= MIN_LISTING_PRICE) {
        this._commission.hidden = false;
        this._commission.textContent = 'Your bike will be listed at ' + formatPrice(applyCommission(listingPrice)) +
          ' (includes ' + getCommissionPercent(listingPrice) + '% commission). You’ll receive ' + formatPrice(listingPrice) + ' when sold.';
      } else {
        this._commission.hidden = true;
        this._commission.textContent = '';
      }
    }
  }

  window.customElements.define('nl-price-grade', NlPriceGrade);
})();
